/* =========================================================
   src/shared/dropboxClient.ts
   App → functions/dropbox (per-person Dropbox connection).
   The server builds every Dropbox path itself from the person's folder
   setting; the app only sends the customer fields + file text.
   ========================================================= */
import type { SupabaseClient } from "@supabase/supabase-js";

/** The function's own error text. supabase-js reports any non-2xx as
 *  "Edge Function returned a non-2xx status code" and hides the body in
 *  error.context (a Response) — read it so the screen says what failed. */
export async function functionErrorText(error: any): Promise<string> {
  const ctx = error?.context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const body = await ctx.clone().json();
      if (body?.error) {
        return typeof body.error === "string" ? body.error : JSON.stringify(body.error);
      }
    } catch {
      /* not JSON — fall through */
    }
  }
  return String(error?.message || error || "Unknown error");
}

export type DropboxStatus = {
  /** Server is set up and this login may use Dropbox. */
  available: boolean;
  connected: boolean;
  accountName: string | null;
  accountEmail: string | null;
  folderTemplate: string;
  /** e.g. "/2026 Potentials/Woodbury Sabo/Woodbury_Sabo.est" */
  examplePath: string;
};

export const DROPBOX_OFF: DropboxStatus = {
  available: false,
  connected: false,
  accountName: null,
  accountEmail: null,
  folderTemplate: "",
  examplePath: "",
};

type Call<T> = { ok: true; data: T } | { ok: false; error: string };

async function call<T>(supabase: SupabaseClient, body: Record<string, unknown>): Promise<Call<T>> {
  try {
    const { data, error } = await supabase.functions.invoke("dropbox", { body });
    if (error) return { ok: false, error: await functionErrorText(error) };
    if (data?.error) return { ok: false, error: String(data.error) };
    return { ok: true, data: data as T };
  } catch (err: any) {
    return { ok: false, error: err?.message || String(err) };
  }
}

/** Any failure → DROPBOX_OFF, so the phone falls back to the email export. */
export async function getDropboxStatus(supabase: SupabaseClient): Promise<DropboxStatus> {
  const r = await call<DropboxStatus>(supabase, { action: "status" });
  return r.ok ? { ...DROPBOX_OFF, ...r.data } : DROPBOX_OFF;
}

/** Leaves the app for Dropbox's Allow page; Dropbox → the callback
 *  function → back to this page with ?dropbox_code=…&dropbox_state=…,
 *  which finishDropboxConnect completes. */
export async function startDropboxConnect(supabase: SupabaseClient): Promise<Call<null>> {
  const returnTo = `${window.location.origin}${window.location.pathname}`;
  const r = await call<{ url: string }>(supabase, { action: "connect_url", returnTo });
  if (!r.ok) return r;
  window.location.assign(r.data.url);
  return { ok: true, data: null };
}

export function finishDropboxConnect(supabase: SupabaseClient, code: string, state: string) {
  return call<DropboxStatus>(supabase, { action: "finish", code, state });
}

export type DropboxFileEntry = { name: string; path: string; modified: string; size: number };

export type DropboxFolderList = {
  /** "/" for the top of their Dropbox. */
  path: string;
  folders: { name: string; path: string }[];
  /** Estimate files (.est / legacy .DUest), newest first — only when asked. */
  files?: DropboxFileEntry[];
  /** The path contains this year (offer "next year's folder each January"). */
  hasYear: boolean;
};

export function listDropboxFolders(
  supabase: SupabaseClient,
  path: string,
  opts: { includeFiles?: boolean } = {}
) {
  return call<DropboxFolderList>(supabase, {
    action: "list_folders",
    path,
    includeFiles: !!opts.includeFiles,
  });
}

/** Download an estimate file's text (the server fetches it, so files that
 *  aren't downloaded to the phone open fine). */
export function openDropboxFile(supabase: SupabaseClient, path: string) {
  return call<{ name: string; path: string; content: string }>(supabase, { action: "open", path });
}

/** The folder a browser should open on: the person's save folder, without
 *  the per-job folder and the file name (from the server's example path). */
export function dropboxStartFolder(status: DropboxStatus): string {
  const parts = status.examplePath.split("/").filter(Boolean);
  parts.pop(); // file
  if (/\{Town\}|\{Last\}/.test(status.folderTemplate.split("/").pop() || "")) parts.pop();
  return "/" + parts.join("/");
}

/** Save a folder chosen in the picker. The server turns it into the
 *  stored template (this year → {year} when rollYear; "{Town} {Last}"
 *  appended when perJobFolder). */
export function setDropboxFolder(
  supabase: SupabaseClient,
  pick: { pickedPath: string; perJobFolder: boolean; rollYear: boolean }
) {
  return call<DropboxStatus>(supabase, { action: "set_folder", ...pick });
}

export function disconnectDropbox(supabase: SupabaseClient) {
  return call<DropboxStatus>(supabase, { action: "disconnect" });
}

export async function saveEstimateToDropbox(
  supabase: SupabaseClient,
  args: {
    town?: string;
    lastName?: string;
    estimateName?: string;
    content: string;
    /** The Dropbox file this estimate came from / was last saved to. */
    path?: string;
  }
): Promise<Call<{ path: string }>> {
  const r = await call<{ ok: boolean; path: string }>(supabase, { action: "save", ...args });
  return r.ok ? { ok: true, data: { path: String(r.data.path || "") } } : r;
}

// ---------------------------------------------------------------------------
// Coming back from Dropbox
// ---------------------------------------------------------------------------

export type DropboxReturn =
  | { kind: "code"; code: string; state: string }
  | { kind: "denied" }
  | { kind: "error" };

/** Read (and strip from the address bar) what the callback appended, so a
 *  reload or a shared URL never replays the code. */
export function takeDropboxReturn(): DropboxReturn | null {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const code = url.searchParams.get("dropbox_code");
  const state = url.searchParams.get("dropbox_state");
  const flag = url.searchParams.get("dropbox");
  if (!code && !flag) return null;
  ["dropbox_code", "dropbox_state", "dropbox"].forEach((k) => url.searchParams.delete(k));
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  if (code && state) return { kind: "code", code, state };
  return flag === "denied" ? { kind: "denied" } : { kind: "error" };
}

// ---------------------------------------------------------------------------
// Which Dropbox file an estimate belongs to (this phone)
// ---------------------------------------------------------------------------
// Set when an estimate is opened from Dropbox or saved to it, keyed by
// estimateId, so the next Save to Dropbox goes back to that same file even
// after the app restarts or the customer's name is edited. Per-device on
// purpose: it's a convenience; without it a save simply uses the folder
// setting. Kept small (newest 200).

const FILE_MAP_KEY = "du_dropbox_files";

function readFileMap(): Record<string, { path: string; at: number }> {
  try {
    const raw = window.localStorage.getItem(FILE_MAP_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function getDropboxFileFor(estimateId: string): string | null {
  if (!estimateId) return null;
  const hit = readFileMap()[estimateId];
  return hit && typeof hit.path === "string" ? hit.path : null;
}

export function rememberDropboxFile(estimateId: string, path: string): void {
  if (!estimateId || !path) return;
  try {
    const map = readFileMap();
    map[estimateId] = { path, at: Date.now() };
    const keep = Object.entries(map)
      .sort((a, b) => (b[1]?.at || 0) - (a[1]?.at || 0))
      .slice(0, 200);
    window.localStorage.setItem(FILE_MAP_KEY, JSON.stringify(Object.fromEntries(keep)));
  } catch {
    /* storage full / blocked — saves fall back to the folder setting */
  }
}
